import { parquetRead } from "hyparquet";
/**
 * Utility functions for processing Parquet files.
 * Reference to repo: https://github.com/noworneverev/graphrag-visualizer
 */

export class AsyncBuffer {
  constructor(buffer) {
    this.buffer = buffer;
  }

  async slice(start, end) {
    return this.buffer.slice(start, end);
  }

  get byteLength() {
    return this.buffer.byteLength;
  }
}

const parseValue = (value, type) => {
  return Number(value);
};


/**
 * Reads a Parquet file and returns its content as an array based on the specified schema.
 * @param {File} file - The Parquet file to read.
 * @param {string} schema - The schema to use for reading the file.
 * @returns {Promise<Array>} - A promise that resolves to an array of objects representing the rows in the file.
 */
export async function readParquetFile(file, schema) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const asyncBuffer = new AsyncBuffer(arrayBuffer);

    return new Promise((resolve, reject) => {
      const options = {
        file: asyncBuffer,
        rowFormat: 'object',
        onComplete: (rows) => {          
          if (schema === "entity") {
            resolve(
              rows.map((row) => ({
                id: row["id"],
                human_readable_id: parseValue(row["human_readable_id"], "number"),
                title: row["title"],
                display_name: row["display_name"],
                type: row["type"],
                category_tags: row["category_tags"],
                context_subtype: row["context_subtype"],
                concept_type: row["concept_type"],
                study_name: row["study_name"],
                event_time: row["event_time"],
                aliases: row["aliases"],
                sources: row["sources"],
                source_count: row["source_count"],
                source_roles: row["source_roles"],
                source_mentions: row["source_mentions"],
                needs_review: row["needs_review"],
                description: row["description"],
                text_unit_ids: row["text_unit_ids"],
              }))
            );
          } else if (schema === "relationship") {
            resolve(
              rows.map((row) => ({
                id: row["id"],
                human_readable_id: Number(row["human_readable_id"]),
                source: row["source"],
                target: row["target"],
                description: row["description"],
                weight: row["weight"],
                combined_degree: Number(row["combined_degree"]),
                text_unit_ids: row["text_unit_ids"],
                type: "RELATED", // Custom field to match neo4j
              }))
            );
          } else if (schema === "document") {
            resolve(
              rows.map((row) => ({
                id: row["id"],
                human_readable_id: Number(row["human_readable_id"]),                
                title: row["title"],
                text: row["text"],
                text_unit_ids: row["text_unit_ids"],           
              }))
            );
          } else if (schema === "text_unit") {
            resolve(
              rows.map((row) => ({
                id: row["id"],
                human_readable_id: Number(row["human_readable_id"]),                
                text: row["text"],
                n_tokens: Number(row["n_tokens"]),                
                document_ids: row["document_ids"] || (row["document_id"] ? [row["document_id"]] : []),
                entity_ids: row["entity_ids"],
                relationship_ids: row["relationship_ids"],
              }))
            );
          } else if (schema === "community") {
            resolve(
              rows.map((row) => ({
                id: row["id"],
                human_readable_id: parseValue(row["human_readable_id"], "number"),     
                community: parseValue(row["community"], "number"),
                parent: parseValue(row["parent"], "number"),
                children: row["children"].map((child) => parseValue(child, "number")),        
                level: parseValue(row["level"], "number"),                
                title: row["title"],
                entity_ids: row["entity_ids"],
                relationship_ids: row["relationship_ids"],
                text_unit_ids: row["text_unit_ids"],
                period: row["period"],                
                size: parseValue(row["size"], "number"),
              }))
            );
          } else if (schema === "community_report") {
            resolve(
              rows.map((row) => ({
                id: row["id"],
                human_readable_id: parseValue(row["human_readable_id"], "number"),
                community: parseValue(row["community"], "number"),       
                parent: parseValue(row["parent"], "number"),
                level: parseValue(row["level"], "number"),                
                title: row["title"],
                summary: row["summary"],
                full_content: row["full_content"],
                rank: row["rank"],
                rank_explanation: row["rank_explanation"],
                findings: row["findings"],
                full_content_json: row["full_content_json"],
                period: row["period"],                
                size: parseValue(row["size"], "number"),            
              }))
            );
          } else if (schema === "covariate") {
            resolve(
              rows.map((row) => ({
                id: row["id"],
                human_readable_id: parseValue(row["human_readable_id"], "number"),                
                covariate_type: row["covariate_type"],
                type: row["type"],
                description: row["description"],
                subject_id: row["subject_id"],
                object_id: row["object_id"],
                status: row["status"],
                start_date: row["start_date"],
                end_date: row["end_date"],
                source_text: row["source_text"],
                text_unit_id: row["text_unit_id"],              
              }))
            );
          } else {
            resolve(
              rows.map((row) => ({
                ...row
              }))
            );
          }
        },
      };
      parquetRead(options).catch(reject);
    });
  } catch (err) {
    console.error("Error reading Parquet file", err);
    return [];
  }
}
